import { useHostingSites, useRefreshHostingSites } from '@/hooks/useHostingSites'
import { Button, SectionHeader, Skeleton, Empty, Badge, Card } from '@/components/ui'
import { Globe, RefreshCw, ExternalLink, Shield, ShieldOff } from 'lucide-react'
import { entityColor, cn } from '@/lib/utils'
import { useApps } from '@/hooks/useApps'
import { Link } from '@tanstack/react-router'

export function HostingSitesPage() {
  const { data: sites, isLoading } = useHostingSites()
  const { data: apps } = useApps()
  const refresh = useRefreshHostingSites()

  const groups = (sites ?? []).reduce<Record<string, typeof sites>>((acc, s) => {
    (acc[s.entity] ??= []).push(s)
    return acc
  }, {} as Record<string, typeof sites>)

  const totalCustom = (sites ?? []).filter((s) => s.custom_domains.length > 0).length
  const projectCount = new Set((sites ?? []).map((s) => s.project_id)).size

  return (
    <div className="space-y-6">
      <SectionHeader
        eyebrow="Hosting Inventory"
        title={`${sites?.length ?? 0} Firebase Hosting sites`}
        description={`Across ${projectCount} ARH-family projects · ${totalCustom} with custom domains`}
        action={
          <Button variant="gold-outline" loading={refresh.isPending} onClick={() => refresh.mutate()}>
            <RefreshCw size={14} /> Refresh from Firebase
          </Button>
        }
      />

      {isLoading ? (
        <div className="grid grid-cols-2 gap-4">
          {Array.from({ length: 6 }, (_, i) => <Skeleton key={i} className="h-40" />)}
        </div>
      ) : (sites?.length ?? 0) === 0 ? (
        <Empty
          icon={<Globe size={48} />}
          title="No hosting sites indexed"
          description='Click "Refresh from Firebase" to fan out across all ARH-family projects.'
          action={<Button onClick={() => refresh.mutate()} loading={refresh.isPending}>Refresh now</Button>}
        />
      ) : (
        <div className="space-y-8">
          {Object.entries(groups).map(([entity, list]) => {
            const eC = entityColor(entity)
            return (
              <section key={entity}>
                <div className="flex items-center gap-3 mb-4">
                  <span className={cn('font-mono text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 rounded border', eC.bg, eC.text, eC.border)}>
                    {entity}
                  </span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-cream/55">
                    {list?.length} sites
                  </span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {(list ?? []).map((s) => {
                    const linkedApp = apps?.find((a) => a.firebase_site === s.site_id || a.firebase_project === s.project_id)
                    return (
                      <Card key={`${s.project_id}-${s.site_id}`} className="!p-4 space-y-3">
                        <div>
                          <div className="font-heading text-base text-cream truncate">{s.site_id}</div>
                          <div className="font-mono text-[10px] text-cream/45 truncate">{s.project_id}</div>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                          {s.custom_domains.length > 0 ? (
                            <Badge tone="success"><Shield size={10} /> {s.custom_domains.length} domain{s.custom_domains.length === 1 ? '' : 's'}</Badge>
                          ) : (
                            <Badge tone="cream"><ShieldOff size={10} /> default only</Badge>
                          )}
                          {linkedApp && (
                            <Link to="/apps/$slug" params={{ slug: linkedApp.slug }}>
                              <Badge tone="gold">→ {linkedApp.name}</Badge>
                            </Link>
                          )}
                        </div>
                        {s.custom_domains.length > 0 && (
                          <div className="space-y-1">
                            {s.custom_domains.map((d) => (
                              <a key={d.domain} href={`https://${d.domain}`} target="_blank" rel="noreferrer" className="block bg-navy/40 border border-cream/8 rounded px-2 py-1.5 text-xs text-gold-soft hover:bg-navy/60 transition-colors">
                                {d.domain} <span className="font-mono text-[10px] text-cream/45 ml-1">{d.status}</span>
                              </a>
                            ))}
                          </div>
                        )}
                        <a href={s.default_url} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-xs text-cream/55 hover:text-cream font-mono">
                          <ExternalLink size={11} /> {s.default_url.replace(/^https?:\/\//, '')}
                        </a>
                      </Card>
                    )
                  })}
                </div>
              </section>
            )
          })}
        </div>
      )}
    </div>
  )
}
